import {
    Alert,
    Checkbox,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Radio,
    RadioGroup,
    Tab,
    Tabs,
    TextField,
} from "@mui/material";
import {useMemo, useState} from "react";
import {useAsyncAction} from "nate-react-api-helpers";
import {Dialog2} from "../../../../../misc/Dialog2";
import {SmoothActionBar} from "../../../../../misc/SmoothActionBar";
import {ElevationNamingItem, ElevationRenames, ElevationSelection} from "../ElevationApp";
import {SavePreflight} from "../../../../../api/elevationTool/ThreePartElevation";

// what the user settles on the dialog, handed back to ElevationApp.save.
export type ElevationSaveResolution = {
    name: string;
    confirm: boolean;
    identityResolution?: "update-all";
    applyDimensionsTo: number[];
    // the elevations saved for this opening only, each under its own name. null updates the
    // shared rows.
    renames: ElevationRenames | null;
};

type ElevationKind = ElevationNamingItem["kind"];

type SectionKey = "name" | "dimensions" | "linked" | "hinges" | "slots";

// the one prompt shown before an elevation save that needs anything from the user: a name for
// a not-yet-saved elevation, an overwrite confirmation, whether a shared elevation updates all
// its openings or only this one, the nominal-dimension write-back, and the hardware group split
// for a hinge layout change.
// They're gathered by ElevationApp.preflight so the user
// answers everything once instead of one dialog per round-trip. Sections become tabs as soon
// as there's more than one.
export function SaveElevationDialog(props: {
    defaultName: string;
    openingName: string;
    // the selected elevations' naming state (which need a name, which keep one).
    elevations: ElevationNamingItem[];
    needsName: boolean;
    preflight: SavePreflight;
    onCancel(): void;
    // runs the save. Resolves with a fresh preflight when the save came back with a conflict
    // of its own (something changed since the preflight ran), else null once it's saved.
    onApply(input: ElevationSaveResolution): Promise<SavePreflight | null>;
}) {
    const [name, setName] = useState(props.defaultName);
    const [preflight, setPreflight] = useState(props.preflight);
    const [linkMode, setLinkMode] = useState<"all" | "only">("all");
    // the "only for this opening" name of each already-saved elevation.
    const [renameNames, setRenameNames] = useState<{[k in ElevationKind]?: string}>(() => {
        const out: {[k in ElevationKind]?: string} = {};
        props.elevations.forEach(e => {
            if(e.existingName) out[e.kind] = `${e.existingName} - ${props.openingName}`;
        });
        return out;
    });
    // the openings ticked for the dimension write-back, all on by default.
    const [applyTo, setApplyTo] = useState<number[]>(() => (props.preflight.dimensionChange?.openings ?? []).map(o => o.id));

    const trimmed = name.trim();
    const nameConflict = preflight.nameConflict;
    const sharing = preflight.sharedIdentityConflict;
    const shared = preflight.sharedElevations;
    // the frame identity check is anchored on the screen, so on its own it marks just that.
    const sharedKinds: ElevationSelection | null = shared
        ? {screen: shared.screen, door: shared.door, hardware: shared.hardware}
        : sharing ? {screen: true, door: false, hardware: false} : null;
    const hasLinked = !!sharedKinds;
    const linkedOpenings = shared?.openings ?? (sharing?.openings ?? []).filter(n => n !== props.openingName);
    const onlyThis = linkMode === "only" && !!sharedKinds;
    // every already-saved elevation takes its own name; a shared one moves to a new row.
    const renamable = props.elevations.filter(e => e.existingName);
    const renames: ElevationRenames | null = onlyThis ? Object.fromEntries(renamable.map(e => [
        e.kind, {name: renameNames[e.kind] ?? "", newRow: !!sharedKinds?.[e.kind]},
    ])) : null;
    const renamesFilled = renamable.every(e => !!renameNames[e.kind]?.trim());
    // a collision on one of the per-elevation names shows on the Linked openings tab instead.
    const renameConflict = onlyThis && !!nameConflict && renamable.some(e => renameNames[e.kind]?.trim() === nameConflict.name);
    // renaming a row onto a taken name can't overwrite (see drw existingByName.renamesExisting),
    // so the user must pick another name.
    const renameBlocked = renameConflict && renamable.some(e => !sharedKinds?.[e.kind] && renameNames[e.kind]?.trim() === nameConflict?.name);
    const overwrite = !!nameConflict && !renameBlocked;
    const dimensions = preflight.dimensionChange;
    const hinges = preflight.hingeGroupSplit;
    const slotUnlink = preflight.slotUnlink;
    // an absent Go slice arrives as null, so normalize before reading either list.
    const requiredOpenings = dimensions?.required ?? [];
    const optionalOpenings = dimensions?.openings ?? [];

    const sections = useMemo(() => {
        const out: {key: SectionKey; label: string}[] = [];
        if(hasLinked) out.push({key: "linked", label: "Linked openings"});
        if(props.needsName || (nameConflict && !renameConflict)) out.push({key: "name", label: "Name"});
        if(dimensions) out.push({key: "dimensions", label: "Dimensions"});
        if(hinges) out.push({key: "hinges", label: "Hinges"});
        if(slotUnlink) out.push({key: "slots", label: "Door slots"});
        return out;
    }, [hasLinked, props.needsName, nameConflict, renameConflict, dimensions, hinges, slotUnlink]);

    const [tab, setTab] = useState<SectionKey>(sections[0]?.key ?? "name");
    // a section can appear after the first save (e.g. a name collision), so never leave the
    // tab pointing at one that's no longer rendered.
    const activeTab = sections.some(s => s.key === tab) ? tab : (sections[0]?.key ?? "name");

    const apply = useAsyncAction(async (input: {confirm: boolean}) => {
        const next = await props.onApply({
            name: trimmed,
            confirm: input.confirm,
            identityResolution: sharing && !onlyThis ? "update-all" : undefined,
            applyDimensionsTo: applyTo,
            renames: renames,
        });

        // a save never reports the shared elevations, so keep them to hold the user's choice.
        if(next) setPreflight(p => ({...next, sharedElevations: next.sharedElevations ?? p.sharedElevations}));
    }, [props.onApply, trimmed, sharing, onlyThis, renames, applyTo]);

    const canApply = (!props.needsName || !!trimmed) && (!onlyThis || renamesFilled) && !renameBlocked;

    const toggleOpening = (id: number) => {
        setApplyTo(list => list.includes(id) ? list.filter(x => x !== id) : list.concat(id));
    };

    return (
        <Dialog2
            open
            onClose={props.onCancel}
            maxWidth="sm"
            fullWidth
            onEnterPress={() => { if(canApply) apply.callback({confirm: overwrite}); }}
        >
            <DialogTitle>{overwrite ? "Overwrite elevation?" : "Save elevation"}</DialogTitle>

            {sections.length > 1 && (
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setTab(v)}
                    variant="fullWidth"
                    sx={{borderBottom: 1, borderColor: "divider"}}
                >
                    {sections.map(s => <Tab key={s.key} value={s.key} label={s.label} />)}
                </Tabs>
            )}

            <DialogContent>
                {activeTab === "name" && (
                    <NameSection
                        name={name}
                        onNameChange={value => {
                            setName(value);
                            // editing the name clears a prior collision so Apply re-checks it.
                            setPreflight(p => ({...p, nameConflict: null}));
                        }}
                        elevations={props.elevations}
                        renamed={onlyThis}
                        conflict={renameConflict ? null : nameConflict}
                    />
                )}

                {activeTab === "linked" && (
                    <>
                        <DialogContentText>
                            This will update {linkedOpenings.length + 1} openings
                            ({[props.openingName, ...linkedOpenings].join(", ")}).
                        </DialogContentText>
                        <RadioGroup
                            value={linkMode}
                            onChange={e => setLinkMode(e.target.value as "all" | "only")}
                            sx={{mt: 1}}
                        >
                            <FormControlLabel
                                value="all"
                                control={<Radio size="small" />}
                                label={`Continue — update all ${linkedOpenings.length + 1}`}
                            />
                            <FormControlLabel
                                value="only"
                                control={<Radio size="small" />}
                                label={`Only for ${props.openingName} — save under new names`}
                            />
                        </RadioGroup>
                        {onlyThis && (
                            <>
                                {renameConflict && nameConflict && (
                                    <Alert severity="warning" sx={{my: 1}}>
                                        There's already an elevation saved under "{nameConflict.name}"
                                        {nameConflict.openings.length > 0 && <> — affects openings {nameConflict.openings.join(", ")}</>}
                                        {renameBlocked && <>. A renamed elevation can't overwrite it — pick another name.</>}
                                    </Alert>
                                )}
                                {renamable.map((e, i) => (
                                    <TextField
                                        key={e.kind}
                                        autoFocus={i === 0}
                                        fullWidth
                                        size="small"
                                        sx={{mt: 2}}
                                        label={`${e.label} elevation name`}
                                        value={renameNames[e.kind] ?? ""}
                                        error={renameConflict && renameNames[e.kind]?.trim() === nameConflict?.name}
                                        helperText={sharedKinds?.[e.kind]
                                            ? `New elevation for ${props.openingName}. The other openings keep "${e.existingName}".`
                                            : `Renames "${e.existingName}". No other opening uses it.`}
                                        onChange={ev => {
                                            const value = ev.target.value;
                                            setRenameNames(r => ({...r, [e.kind]: value}));
                                            // editing a name clears a prior collision so Save re-checks it.
                                            setPreflight(p => ({...p, nameConflict: null}));
                                        }}
                                    />
                                ))}
                            </>
                        )}
                    </>
                )}

                {activeTab === "dimensions" && dimensions && (
                    <>
                        <DialogContentText>
                            You changed the nominal size of {dimensions.opening}.
                        </DialogContentText>
                        <table style={{margin: "12px 0", fontSize: 13}}>
                            <tbody>
                                {(dimensions.changes ?? []).map(c => (
                                    <tr key={c.field}>
                                        <td style={{paddingRight: 16, fontWeight: 500}}>{c.field}</td>
                                        <td style={{paddingRight: 8, opacity: 0.6}}>{c.from || "(none)"}</td>
                                        <td style={{paddingRight: 8}}>&rarr;</td>
                                        <td>{c.to}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {requiredOpenings.length > 0 && (
                            <Alert severity="info" sx={{mb: 2}}>
                                {requiredOpenings.map(o => o.name).join(", ")}
                                {requiredOpenings.length === 1 ? " is" : " are"} in the same
                                hardware group, which can't hold openings of different nominal
                                sizes — so {requiredOpenings.length === 1 ? "it gets" : "they get"} the
                                new size too.
                            </Alert>
                        )}

                        {optionalOpenings.length > 0 && (
                            <>
                                <DialogContentText>
                                    Pick the other openings that should get the same size.
                                </DialogContentText>
                                {optionalOpenings.map(o => (
                                    <div key={o.id}>
                                        <FormControlLabel
                                            control={<Checkbox
                                                size="small"
                                                checked={applyTo.includes(o.id)}
                                                onChange={() => toggleOpening(o.id)}
                                            />}
                                            label={o.name}
                                        />
                                    </div>
                                ))}
                            </>
                        )}
                    </>
                )}

                {activeTab === "hinges" && hinges && (
                    <>
                        <DialogContentText>
                            The hinge layout is different from hardware heading {hinges.group}. The
                            hardware heading holds the hinge quantities, so saving moves
                            {" "}{(hinges.moving ?? []).join(", ")} into a new hardware heading.
                        </DialogContentText>
                        {(hinges.staying ?? []).length > 0 && (
                            <Alert severity="info" sx={{mt: 2}}>
                                {(hinges.staying ?? []).join(", ")} keep{(hinges.staying ?? []).length === 1 ? "s" : ""} hardware
                                heading {hinges.group} and {(hinges.staying ?? []).length === 1 ? "its" : "their"} hinge layout.
                            </Alert>
                        )}
                    </>
                )}

                {activeTab === "slots" && slotUnlink && (
                    <DialogContentText>
                        This screen no longer has the door slots that
                        {" "}{(slotUnlink.openings ?? []).join(", ")} fill. Saving unlinks
                        {" "}{(slotUnlink.openings ?? []).length === 1 ? "that opening" : "those openings"} from this frame.
                    </DialogContentText>
                )}
            </DialogContent>

            <SmoothActionBar
                loading={apply.loading}
                error={apply.error}
                onCancel={props.onCancel}
                onSubmit={() => apply.callback({confirm: overwrite})}
                submitDisabled={!canApply}
                actionName={overwrite ? "Overwrite & Save" : "Save"}
                submittingWord="Saving…"
                submitColor={overwrite ? "error" : undefined}
            />
        </Dialog2>
    );
}

// the name input plus the per-elevation "new / keep" breakdown, or the overwrite warning when
// the name is already taken.
function NameSection(props: {
    name: string;
    onNameChange(value: string): void;
    elevations: ElevationNamingItem[];
    // true when the saved elevations take the names on the Linked openings tab.
    renamed: boolean;
    conflict: {name: string; openings: string[]} | null;
}) {
    const trimmed = props.name.trim();
    const needing = props.elevations.filter(e => !e.existingName);
    const restNote = props.renamed ? "The rest use the names on the Linked openings tab." : "The rest keep their existing name.";

    return (
        <>
            {props.conflict && (
                <Alert severity="warning" sx={{mb: 2}}>
                    There's already an elevation saved under "{props.conflict.name}"
                    {props.conflict.openings.length > 0 && <> — affects openings {props.conflict.openings.join(", ")}</>}
                </Alert>
            )}

            {needing.length > 0 && (
                <DialogContentText>
                    {needing.length === props.elevations.length
                        ? "Names the frame, door and hardware elevations."
                        : <>Names {needing.map(e => e.label).join(" & ")}. {restNote}</>}
                </DialogContentText>
            )}

            <div style={{height: 8}} />
            <TextField
                autoFocus
                fullWidth
                label="Elevation name"
                value={props.name}
                onChange={e => props.onNameChange(e.target.value)}
            />
            <div style={{height: 12}} />
            {props.elevations.map(e => (
                <div key={e.kind} style={{display: "flex", justifyContent: "space-between", padding: "2px 0"}}>
                    <span style={{fontWeight: 500}}>{e.label}</span>
                    {e.existingName
                        ? <span style={{opacity: 0.6}}>{props.renamed ? "see Linked openings" : `keep "${e.existingName}"`}</span>
                        : <span>new: "{trimmed || "…"}"</span>}
                </div>
            ))}
        </>
    );
}
